import { useCallback, useState } from 'react';
import {
  EditableContainer,
  Grid,
  Panel,
  PanelContent,
  Typography,
  TypographyWithTooltip,
  WidgetContainer,
  S5,
  S6,
  AB3,
  Select,
  InputAdornment,
  GlobeIcon,
  MenuItem,
  useTranslations,
  CircularProgress,
  useSnackbar,
  TSelectProps,
} from '@uniqkey-frontend/shared-app';
import {
  useGetCurrentUser,
  useUpdateCurrentUser,
  useLanguages,
} from '../../../../hooks/reactQuery';
import EditProfileModal, { IEditProfileModalSubmit } from '../EditProfileModal';

const SELECT_SX = { width: 215 };
const SELECT_START_ADORNMENT = (
  <InputAdornment position="start">
    <GlobeIcon color={AB3} />
  </InputAdornment>
);

const ProfileModule = () => {
  const { t } = useTranslations();
  const { showError, showSuccess } = useSnackbar();

  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const handleEditProfileModalOpen = useCallback(() => setIsEditProfileModalOpen(true), []);
  const handleEditProfileModalClose = useCallback(() => setIsEditProfileModalOpen(false), []);

  const {
    data: currentUser, isLoading, isError,
  } = useGetCurrentUser();
  const { mutate, isLoading: isCurrentUserUpdating } = useUpdateCurrentUser({
    useOptimisticUpdates: true,
  });

  const { languages, isLoading: areLanguagesLoading } = useLanguages();

  const handleEditProfile = useCallback((value: IEditProfileModalSubmit) => {
    mutate(
      { ...currentUser!, ...value },
      {
        onError: () => showError({ text: t('common.somethingWentWrong') }),
        onSuccess: () => {
          showSuccess({ text: t('profilePage.toast.profileUpdated') });
          handleEditProfileModalClose();
        },
      },
    );
  }, [mutate, currentUser, handleEditProfileModalClose, showError, showSuccess, t]);

  const handleLanguageChange = useCallback<
    NonNullable<TSelectProps['onChange']>
  >((event) => {
    if (isCurrentUserUpdating) {
      return;
    }
    mutate(
      { ...currentUser!, language: event.target.value as string },
      {
        onError: () => showError({ text: t('common.somethingWentWrong') }),
        onSuccess: () => showSuccess({ text: t('profilePage.toast.languageUpdated') }),
      },
    );
  }, [isCurrentUserUpdating, mutate, currentUser, showError, showSuccess, t]);

  if (isError) {
    return null;
  }

  if (isLoading || areLanguagesLoading) {
    return (
      <Panel className="min-height-100-percent">
        <PanelContent className="min-height-100-percent">
          <Grid
            container
            alignItems="center"
            justifyContent="center"
            className="min-height-100-percent"
          >
            <CircularProgress />
          </Grid>
        </PanelContent>
      </Panel>
    );
  }

  const { name, email, language } = currentUser!;

  return (
    <Panel className="height-100-percent">
      <PanelContent p={3} pt={3.5}>
        <Grid container gap={2}>
          <WidgetContainer item withShadow p={0} xs={12} md={6}>
            <EditableContainer
              item
              container
              flexDirection="column"
              p={3}
              rowGap={3}
              truncate={false}
              onClick={handleEditProfileModalOpen}
            >
              <Grid container flexDirection="column">
                <Typography variant="caption" color={S5} mb={1}>
                  {t('profilePage.name')}
                </Typography>
                <TypographyWithTooltip variant="body3" color={S6}>
                  {name}
                </TypographyWithTooltip>
              </Grid>
              <Grid container flexDirection="column">
                <Typography variant="caption" color={S5} mb={1}>
                  {t('profilePage.email')}
                </Typography>
                <TypographyWithTooltip variant="body3" color={S6}>
                  {email}
                </TypographyWithTooltip>
              </Grid>
            </EditableContainer>
          </WidgetContainer>
          <WidgetContainer item withShadow p={3} xs={12} md>
            <Grid container flexDirection="column">
              <Typography variant="caption" color={S5} mb={1}>
                {t('profilePage.changeLanguage')}
              </Typography>
              <Select
                value={language}
                onChange={handleLanguageChange}
                sx={SELECT_SX}
                startAdornment={SELECT_START_ADORNMENT}
              >
                {
                  languages.map((currLanguage) => (
                    <MenuItem key={currLanguage.code} value={currLanguage.code}>
                      {currLanguage.localized_name}
                    </MenuItem>
                  ))
                }
              </Select>
            </Grid>
          </WidgetContainer>
        </Grid>
      </PanelContent>
      {isEditProfileModalOpen && (
        <EditProfileModal
          isOpen={isEditProfileModalOpen}
          isLoading={isCurrentUserUpdating}
          onSubmit={handleEditProfile}
          onClose={handleEditProfileModalClose}
          currentUser={currentUser!}
        />
      )}
    </Panel>
  );
};

export default ProfileModule;
